
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

type SubDiscipline = Tables<'sub_disciplines'>;

export function useSubDisciplines(disciplineId?: string) {
  return useQuery({
    queryKey: ['sub_disciplines', disciplineId],
    queryFn: async () => {
      let query = supabase
        .from('sub_disciplines')
        .select('*')
        .order('name');
      if (disciplineId) {
        query = query.eq('discipline_id', disciplineId);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data as SubDiscipline[];
    }
  });
}

export function useCreateSubDiscipline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, description, slug, discipline_id }: Partial<SubDiscipline>) => {
      const { data, error } = await supabase
        .from('sub_disciplines')
        .insert([{ name, description, slug, discipline_id }])
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub_disciplines'] });
    }
  });
}

export function useUpdateSubDiscipline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: Partial<SubDiscipline> & { id: string }) => {
      const { id, ...patch } = updates;
      const { data, error } = await supabase
        .from('sub_disciplines')
        .update(patch)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub_disciplines'] });
    }
  });
}

export function useDeleteSubDiscipline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { error } = await supabase
        .from('sub_disciplines')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub_disciplines'] });
    }
  });
}
